import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { NAV } from "@/constants/testIds";

const Logo = () => (
  <a
    href="#top"
    data-testid={NAV.logo}
    className="group inline-flex items-center"
    aria-label="Polestar Innovation Solutions"
  >
    <span className="relative inline-flex items-center">
      <span className="absolute -inset-x-6 -inset-y-2 rounded-full bg-[#007AFF]/10 blur-2xl opacity-70 group-hover:opacity-100 transition-opacity" />
      <img
        src="/pis-lockup-white.png"
        alt="Polestar Innovation Solutions"
        className="relative h-12 sm:h-14 md:h-16 w-auto object-contain"
      />
    </span>
  </a>
);

const NavLink = ({ href, label, testId, onClick }) => (
  <a
    href={href}
    data-testid={testId}
    onClick={onClick}
    className="relative text-sm text-white/70 hover:text-white transition-colors duration-300"
  >
    <span>{label}</span>
  </a>
);

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#what", label: t.nav.what, testId: NAV.linkWhat },
    { href: "#platforms", label: t.nav.platforms, testId: NAV.linkPlatforms },
    { href: "#cx", label: t.nav.cx, testId: NAV.linkCx },
    { href: "#impact", label: t.nav.impact, testId: NAV.linkImpact },
    { href: "#founder", label: t.nav.founder, testId: NAV.linkFounder },
    { href: "#contact", label: t.nav.contact, testId: NAV.linkContact },
  ];

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050810]/70 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex h-20 md:h-24 items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink key={l.href} {...l} />
            ))}
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={toggle}
              data-testid={NAV.langToggle}
              className="group flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-medium text-white/80 hover:border-[#00F0FF]/40 hover:text-white transition-all"
              aria-label="Toggle language"
            >
              <Languages className="h-3.5 w-3.5" />
              <span className="font-display tracking-wide">
                {lang === "en" ? "AR" : "EN"}
              </span>
            </button>
            <a
              href="#contact"
              data-testid={NAV.ctaPartner}
              className="hidden md:inline-flex items-center rounded-full btn-primary px-4 py-2 text-xs md:text-sm"
            >
              {t.nav.cta}
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid={NAV.mobileToggle}
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-white/10 bg-white/[0.03] text-white/80"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-white/5 bg-[#050810]/95 backdrop-blur-xl"
          >
            <div className="mx-auto max-w-7xl px-5 py-5 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.href}
                  {...l}
                  onClick={() => setOpen(false)}
                />
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex justify-center btn-primary rounded-full px-4 py-2.5 text-sm"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
